import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Loader2,
  Search,
  Users,
  X,
} from 'lucide-react';
import {
  employeeApi,
  locationApi,
  payrollDepartmentApi,
  payrollEmploymentTypesApi,
  policyDocumentApi,
  positionApi,
  PolicyDocumentAudienceRule,
  UpsertPolicyDocumentPayload,
} from '../services/api';
import { Employee, Location, PayrollDepartment, PayrollEmploymentType, Position } from '../types';

const CATEGORIES = [
  { value: 'HrPolicy', label: 'HR Policy' },
  { value: 'HealthAndSafety', label: 'Health & Safety' },
  { value: 'CompanyPolicy', label: 'Company Policy' },
  { value: 'DataAndIt', label: 'Data & IT' },
  { value: 'Operations', label: 'Operations' },
  { value: 'EmployeeHandbook', label: 'Employee Handbook' },
  { value: 'Procedures', label: 'Procedures' },
  { value: 'Other', label: 'Other' },
];

type AudienceFilters = {
  locationIds: number[];
  departmentIds: number[];
  positionIds: number[];
  employmentTypeIds: number[];
};

const emptyFilters = (): AudienceFilters => ({
  locationIds: [],
  departmentIds: [],
  positionIds: [],
  employmentTypeIds: [],
});

const emptyAudience = (): PolicyDocumentAudienceRule => ({
  allEmployees: true,
  locationIds: [],
  positionIds: [],
  departmentIds: [],
  employmentTypeIds: [],
  employeeIds: [],
});

function employeeName(e: Employee) {
  return `${e.firstName} ${e.lastName}`.trim();
}

function isEligibleEmployee(e: Employee) {
  const status = (e as Employee & { employmentStatus?: string }).employmentStatus;
  const statusOk = !status || status === 'Active';
  return e.isActive && statusOk && !!e.profile;
}

function employeeLocationIds(e: Employee): number[] {
  const main = e.profile?.mainLocationId ?? e.locationId;
  const subs = e.profile?.subLocationIds || [];
  return [main, ...subs].filter((id): id is number => typeof id === 'number');
}

function employeePositionIds(e: Employee): number[] {
  const main = e.profile?.mainPositionId ?? e.positionId;
  const subs = e.profile?.subPositionIds || [];
  return [main, ...subs].filter((id): id is number => typeof id === 'number');
}

function employeeDepartmentId(e: Employee): number | undefined {
  return e.profile?.payrollDepartmentId;
}

function employeeEmploymentTypeId(e: Employee): number | undefined {
  return e.profile?.payrollEmploymentTypeId ?? undefined;
}

/** Cascade match: each selected dimension must match (AND). Empty dimension = no constraint. */
function matchesCascade(e: Employee, filters: AudienceFilters): boolean {
  if (filters.locationIds.length > 0) {
    const locs = employeeLocationIds(e);
    if (!filters.locationIds.some((id) => locs.includes(id))) return false;
  }
  if (filters.departmentIds.length > 0) {
    const dept = employeeDepartmentId(e);
    if (dept == null || !filters.departmentIds.includes(dept)) return false;
  }
  if (filters.positionIds.length > 0) {
    const positions = employeePositionIds(e);
    if (!filters.positionIds.some((id) => positions.includes(id))) return false;
  }
  if (filters.employmentTypeIds.length > 0) {
    const typeId = employeeEmploymentTypeId(e);
    if (typeId == null || !filters.employmentTypeIds.includes(typeId)) return false;
  }
  return true;
}

function toggleInList(list: number[], id: number): number[] {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

const DocumentPolicyEditorPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const editingId = id ? Number(id) : null;
  const [step, setStep] = useState<1 | 2>(1);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audienceCount, setAudienceCount] = useState<number | null>(null);

  const [name, setName] = useState('');
  const [category, setCategory] = useState('HrPolicy');
  const [ownerLabel, setOwnerLabel] = useState('');
  const [versionLabel, setVersionLabel] = useState('1.0');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [requiresAck, setRequiresAck] = useState(true);
  const [notifyOnPublish, setNotifyOnPublish] = useState(true);
  const [firstReminderAfterDays, setFirstReminderAfterDays] = useState<number | ''>(7);
  const [repeatReminderEveryDays, setRepeatReminderEveryDays] = useState<number | ''>(7);
  const [maxReminders, setMaxReminders] = useState(5);
  const [file, setFile] = useState<File | null>(null);
  const [existingFileName, setExistingFileName] = useState<string | null>(null);
  const [status, setStatus] = useState('Draft');
  const [allEmployees, setAllEmployees] = useState(true);
  const [filters, setFilters] = useState<AudienceFilters>(emptyFilters());
  const [finalEmployeeIds, setFinalEmployeeIds] = useState<number[]>([]);
  const [matchedQuery, setMatchedQuery] = useState('');
  const [finalQuery, setFinalQuery] = useState('');

  const [locations, setLocations] = useState<Location[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [departments, setDepartments] = useState<PayrollDepartment[]>([]);
  const [employmentTypes, setEmploymentTypes] = useState<PayrollEmploymentType[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);

  useEffect(() => {
    void Promise.all([
      locationApi.getAll(),
      positionApi.getAll(),
      payrollDepartmentApi.getAll(),
      payrollEmploymentTypesApi.list(),
      employeeApi.getAll(),
    ]).then(([locs, pos, deps, types, emps]) => {
      setLocations(locs || []);
      setPositions(pos || []);
      setDepartments(deps || []);
      setEmploymentTypes(types || []);
      setEmployees(emps || []);
    }).catch((err) => console.error(err));
  }, []);

  useEffect(() => {
    if (!editingId) return;
    void policyDocumentApi.get(editingId).then((doc) => {
      setName(doc.name);
      setCategory(doc.category);
      setOwnerLabel(doc.ownerLabel || '');
      setVersionLabel(doc.versionLabel || '1.0');
      setEffectiveDate(doc.effectiveDate || '');
      setReviewDate(doc.reviewDate || '');
      setRequiresAck(doc.requiresAcknowledgement);
      setNotifyOnPublish(doc.notifyOnPublish);
      setFirstReminderAfterDays(doc.firstReminderAfterDays ?? '');
      setRepeatReminderEveryDays(doc.repeatReminderEveryDays ?? '');
      setMaxReminders(doc.maxReminders ?? 5);
      setExistingFileName(doc.fileName || null);
      setStatus(doc.status);
      setAllEmployees(doc.audienceAllEmployees);
      setFilters({
        locationIds: doc.locationIds || [],
        departmentIds: doc.departmentIds || [],
        positionIds: doc.positionIds || [],
        employmentTypeIds: doc.employmentTypeIds || [],
      });
      setFinalEmployeeIds(doc.employeeIds || []);
    }).catch((err) => setError(err?.response?.data?.message || 'Could not load document.'));
  }, [editingId]);

  const eligible = useMemo(() => employees.filter(isEligibleEmployee), [employees]);

  const audiencePayload = useMemo((): PolicyDocumentAudienceRule => {
    if (allEmployees) return emptyAudience();
    // Final list is the distribution audience (named employees). Filters are a builder only.
    return {
      allEmployees: false,
      locationIds: [],
      departmentIds: [],
      positionIds: [],
      employmentTypeIds: [],
      employeeIds: finalEmployeeIds,
    };
  }, [allEmployees, finalEmployeeIds]);

  useEffect(() => {
    if (step !== 2) return;
    const handle = window.setTimeout(() => {
      void policyDocumentApi.audiencePreview(audiencePayload).then((r) => setAudienceCount(r.count)).catch(() => setAudienceCount(null));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [audiencePayload, step]);

  const hasAnyFilter =
    filters.locationIds.length > 0
    || filters.departmentIds.length > 0
    || filters.positionIds.length > 0
    || filters.employmentTypeIds.length > 0;

  const matchedEmployees = useMemo(() => {
    if (allEmployees) return [];
    if (!hasAnyFilter) return [];
    return eligible
      .filter((e) => matchesCascade(e, filters))
      .filter((e) => !finalEmployeeIds.includes(e.id))
      .sort((a, b) => employeeName(a).localeCompare(employeeName(b)));
  }, [allEmployees, hasAnyFilter, eligible, filters, finalEmployeeIds]);

  const matchedVisible = useMemo(() => {
    const q = matchedQuery.trim().toLowerCase();
    if (!q) return matchedEmployees;
    return matchedEmployees.filter((e) => {
      const label = employeeName(e).toLowerCase();
      return label.includes(q) || (e.email || '').toLowerCase().includes(q);
    });
  }, [matchedEmployees, matchedQuery]);

  const finalEmployees = useMemo(() => {
    const set = new Set(finalEmployeeIds);
    return eligible
      .filter((e) => set.has(e.id))
      .sort((a, b) => employeeName(a).localeCompare(employeeName(b)));
  }, [eligible, finalEmployeeIds]);

  const finalVisible = useMemo(() => {
    const q = finalQuery.trim().toLowerCase();
    if (!q) return finalEmployees;
    return finalEmployees.filter((e) => {
      const label = employeeName(e).toLowerCase();
      return label.includes(q) || (e.email || '').toLowerCase().includes(q);
    });
  }, [finalEmployees, finalQuery]);

  /** Employees in scope for counting a filter option (parents already applied). */
  const scopeForLocations = eligible;
  const scopeForDepartments = useMemo(
    () => eligible.filter((e) => matchesCascade(e, { ...emptyFilters(), locationIds: filters.locationIds })),
    [eligible, filters.locationIds]
  );
  const scopeForPositions = useMemo(
    () => eligible.filter((e) => matchesCascade(e, {
      locationIds: filters.locationIds,
      departmentIds: filters.departmentIds,
      positionIds: [],
      employmentTypeIds: [],
    })),
    [eligible, filters.locationIds, filters.departmentIds]
  );
  const scopeForTypes = useMemo(
    () => eligible.filter((e) => matchesCascade(e, {
      locationIds: filters.locationIds,
      departmentIds: filters.departmentIds,
      positionIds: filters.positionIds,
      employmentTypeIds: [],
    })),
    [eligible, filters.locationIds, filters.departmentIds, filters.positionIds]
  );

  const locationOptions = useMemo(() => {
    return locations
      .filter((l) => l.isActive !== false)
      .map((l) => ({
        id: l.id,
        label: l.name,
        count: scopeForLocations.filter((e) => employeeLocationIds(e).includes(l.id)).length,
      }))
      .filter((o) => o.count > 0 || filters.locationIds.includes(o.id))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [locations, scopeForLocations, filters.locationIds]);

  const departmentOptions = useMemo(() => {
    return departments
      .map((d) => ({
        id: d.id,
        label: d.name,
        count: scopeForDepartments.filter((e) => employeeDepartmentId(e) === d.id).length,
      }))
      .filter((o) => o.count > 0 || filters.departmentIds.includes(o.id))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [departments, scopeForDepartments, filters.departmentIds]);

  const positionOptions = useMemo(() => {
    return positions
      .filter((p) => p.isActive !== false)
      .map((p) => ({
        id: p.id,
        label: p.title,
        count: scopeForPositions.filter((e) => employeePositionIds(e).includes(p.id)).length,
      }))
      .filter((o) => o.count > 0 || filters.positionIds.includes(o.id))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [positions, scopeForPositions, filters.positionIds]);

  const employmentTypeOptions = useMemo(() => {
    return employmentTypes
      .filter((t) => t.isActive !== false)
      .map((t) => ({
        id: t.id,
        label: t.displayName,
        count: scopeForTypes.filter((e) => employeeEmploymentTypeId(e) === t.id).length,
      }))
      .filter((o) => o.count > 0 || filters.employmentTypeIds.includes(o.id))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [employmentTypes, scopeForTypes, filters.employmentTypeIds]);

  const pruneFilters = (next: AudienceFilters): AudienceFilters => {
    const afterLoc = eligible.filter((e) => matchesCascade(e, { ...emptyFilters(), locationIds: next.locationIds }));
    const deptIds = new Set(afterLoc.map(employeeDepartmentId).filter((x): x is number => x != null));
    const departmentIds = next.departmentIds.filter((id) => deptIds.has(id));
    const afterDept = eligible.filter((e) => matchesCascade(e, {
      locationIds: next.locationIds,
      departmentIds,
      positionIds: [],
      employmentTypeIds: [],
    }));
    const posIds = new Set(afterDept.flatMap(employeePositionIds));
    const positionIds = next.positionIds.filter((id) => posIds.has(id));
    const afterPos = eligible.filter((e) => matchesCascade(e, {
      locationIds: next.locationIds,
      departmentIds,
      positionIds,
      employmentTypeIds: [],
    }));
    const typeIds = new Set(afterPos.map(employeeEmploymentTypeId).filter((x): x is number => x != null));
    const employmentTypeIds = next.employmentTypeIds.filter((id) => typeIds.has(id));
    return { locationIds: next.locationIds, departmentIds, positionIds, employmentTypeIds };
  };

  const toggleFilter = (key: keyof AudienceFilters, idValue: number) => {
    setAllEmployees(false);
    setFilters((prev) => pruneFilters({ ...prev, [key]: toggleInList(prev[key], idValue) }));
  };

  const moveMatchedToFinal = () => {
    if (matchedEmployees.length === 0) return;
    setAllEmployees(false);
    setFinalEmployeeIds((prev) => {
      const set = new Set(prev);
      matchedEmployees.forEach((e) => set.add(e.id));
      return Array.from(set);
    });
  };

  const removeFromFinal = (idValue: number) => {
    setAllEmployees(false);
    setFinalEmployeeIds((prev) => prev.filter((x) => x !== idValue));
  };

  const clearFinal = () => {
    setAllEmployees(false);
    setFinalEmployeeIds([]);
  };

  const selectAllEmployeesMode = (checked: boolean) => {
    setAllEmployees(checked);
    if (checked) {
      setFilters(emptyFilters());
      setFinalEmployeeIds([]);
    }
  };

  const payload = (): UpsertPolicyDocumentPayload => ({
    name: name.trim(),
    category,
    ownerLabel: ownerLabel.trim() || null,
    requiresAcknowledgement: requiresAck,
    notifyOnPublish,
    firstReminderAfterDays: firstReminderAfterDays === '' ? null : Number(firstReminderAfterDays),
    repeatReminderEveryDays: repeatReminderEveryDays === '' ? null : Number(repeatReminderEveryDays),
    maxReminders,
    versionLabel: versionLabel.trim() || '1.0',
    effectiveDate: effectiveDate || null,
    reviewDate: reviewDate || null,
    audience: audiencePayload,
  });

  const saveDraft = async (thenPublish: boolean) => {
    if (!name.trim()) {
      setError('Document name is required.');
      setStep(1);
      return;
    }
    if (thenPublish && !allEmployees && finalEmployeeIds.length === 0) {
      setError('Add employees to the final distribution list, or choose All employees.');
      setStep(2);
      return;
    }
    if (saving) return;
    setSaving(true);
    setPublishing(thenPublish);
    setError(null);
    try {
      let docId = editingId;
      if (docId) {
        await policyDocumentApi.update(docId, payload());
      } else {
        const created = await policyDocumentApi.create(payload());
        docId = created.id;
      }
      if (file && docId) {
        await policyDocumentApi.uploadFile(docId, file, {
          versionLabel: versionLabel.trim() || '1.0',
          effectiveDate: effectiveDate || undefined,
          reviewDate: reviewDate || undefined,
        });
      }
      if (thenPublish && docId) {
        if (!file && !existingFileName) {
          setError('Upload a file before publishing.');
          return;
        }
        const result = await policyDocumentApi.publish(docId);
        window.alert(result.message);
        navigate(`/documents-policies/${docId}`);
        return;
      }
      navigate(`/documents-policies/${docId}`);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(message || 'Could not save the document.');
    } finally {
      setSaving(false);
      setPublishing(false);
    }
  };

  return (
    <div className={`p-6 mx-auto ${step === 2 ? 'max-w-7xl' : 'max-w-4xl'}`}>
      <h1 className="text-2xl font-semibold text-neutral-900 mb-1">
        {editingId ? 'Edit document' : 'Add document'}
      </h1>
      <p className="text-sm text-neutral-500 mb-6">{step === 1 ? 'Details and file' : 'Distribution'}</p>
      {error && <div className="mb-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800">{error}</div>}

      {step === 1 ? (
        <div className="space-y-4 rounded-xl border border-neutral-200 bg-white p-5">
          <label className="block text-sm">
            <span className="font-medium text-neutral-700">Document name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2" placeholder="Dignity at Work Policy" />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-neutral-700">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2">
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-neutral-700">Upload file (PDF / Word)</span>
            <input type="file" accept=".pdf,.doc,.docx,application/pdf" className="mt-1 block w-full text-sm" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            {existingFileName && !file && <p className="text-xs text-neutral-500 mt-1">Current file: {existingFileName}</p>}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block text-sm">
              <span className="font-medium text-neutral-700">Version</span>
              <input value={versionLabel} onChange={(e) => setVersionLabel(e.target.value)} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2" />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-neutral-700">Effective date</span>
              <input type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2" />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-neutral-700">Review date</span>
              <input type="date" value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2" />
            </label>
          </div>
          <label className="block text-sm">
            <span className="font-medium text-neutral-700">Document owner</span>
            <input value={ownerLabel} onChange={(e) => setOwnerLabel(e.target.value)} className="mt-1 w-full rounded-lg border border-neutral-200 px-3 py-2" placeholder="HR Department" />
          </label>
          <div className="text-sm">
            <span className="font-medium text-neutral-700">Employee acknowledgement</span>
            <div className="mt-2 space-y-1">
              <label className="flex items-center gap-2"><input type="radio" checked={!requiresAck} onChange={() => setRequiresAck(false)} /> No acknowledgement required</label>
              <label className="flex items-center gap-2"><input type="radio" checked={requiresAck} onChange={() => setRequiresAck(true)} /> Employee acknowledgement required</label>
            </div>
            {requiresAck && (
              <p className="mt-2 text-neutral-500">Standard statement: “I confirm that I have read and understood this document.”</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => navigate('/documents-policies')} className="rounded-lg px-4 py-2 text-sm text-neutral-600">Cancel</button>
            <button type="button" disabled={saving} onClick={() => void saveDraft(false)} className="rounded-lg border border-neutral-200 px-4 py-2 text-sm">Save draft</button>
            <button type="button" onClick={() => setStep(2)} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white">Continue to distribution</button>
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
              <label className="inline-flex items-center gap-2 text-sm font-medium text-slate-800">
                <input
                  type="checkbox"
                  checked={allEmployees}
                  onChange={(e) => selectAllEmployeesMode(e.target.checked)}
                  className="rounded border-slate-300"
                />
                All eligible employees
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 tabular-nums">
                  {eligible.length}
                </span>
              </label>
              <div className="text-sm text-slate-600">
                <span className="font-semibold tabular-nums text-slate-900">{audienceCount ?? '…'}</span>
                {' '}will receive
              </div>
            </div>

            {!allEmployees && (
              <div>
                {/* Filters — single horizontal row */}
                <div className="border-b border-slate-100 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-900">Filters</h3>
                    <button
                      type="button"
                      className="text-xs font-medium text-blue-700 hover:underline"
                      onClick={() => setFilters(emptyFilters())}
                    >
                      Clear
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <FilterLevel
                      step={1}
                      title="Location"
                      options={locationOptions}
                      selected={filters.locationIds}
                      onToggle={(idValue) => toggleFilter('locationIds', idValue)}
                    />
                    <FilterLevel
                      step={2}
                      title="Department"
                      options={departmentOptions}
                      selected={filters.departmentIds}
                      onToggle={(idValue) => toggleFilter('departmentIds', idValue)}
                    />
                    <FilterLevel
                      step={3}
                      title="Position"
                      options={positionOptions}
                      selected={filters.positionIds}
                      onToggle={(idValue) => toggleFilter('positionIds', idValue)}
                    />
                    <FilterLevel
                      step={4}
                      title="Employment type"
                      options={employmentTypeOptions}
                      selected={filters.employmentTypeIds}
                      onToggle={(idValue) => toggleFilter('employmentTypeIds', idValue)}
                    />
                  </div>
                </div>

                {/* Employee lists — left / move / right */}
                <div className="grid grid-cols-1 gap-0 md:grid-cols-[1fr_auto_1fr]">
                  <EmployeePanel
                    title="Matching employees"
                    subtitle={hasAnyFilter ? `${matchedEmployees.length} match current filters` : 'Select filters to build a match list'}
                    query={matchedQuery}
                    onQueryChange={setMatchedQuery}
                    employees={matchedVisible}
                    emptyText={hasAnyFilter ? 'No employees match these filters' : 'Choose at least one filter'}
                    accent="slate"
                  />

                  <div className="flex items-center justify-center border-b border-slate-100 bg-slate-50/80 px-3 py-3 md:border-b-0 md:border-x">
                    <button
                      type="button"
                      onClick={moveMatchedToFinal}
                      disabled={matchedEmployees.length === 0}
                      title="Move matching employees to final list"
                      className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-blue-600 text-white shadow-md transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none"
                    >
                      <ArrowRight className="h-5 w-5" />
                    </button>
                  </div>

                  <EmployeePanel
                    title="Final distribution list"
                    subtitle={`${finalEmployees.length} selected`}
                    query={finalQuery}
                    onQueryChange={setFinalQuery}
                    employees={finalVisible}
                    emptyText="Moved employees appear here"
                    accent="blue"
                    onRemove={removeFromFinal}
                    headerAction={
                      finalEmployees.length > 0 ? (
                        <button type="button" onClick={clearFinal} className="text-xs font-medium text-rose-600 hover:underline">
                          Clear all
                        </button>
                      ) : null
                    }
                  />
                </div>
              </div>
            )}

            {allEmployees && (
              <div className="flex items-center gap-3 bg-blue-50/60 px-5 py-8 text-sm text-slate-700">
                <Users className="h-5 w-5 text-blue-700" />
                Every eligible employee ({eligible.length}) will receive this document when published.
              </div>
            )}
          </div>

          <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <label className="flex items-center gap-2 text-sm text-slate-800">
              <input type="checkbox" checked={notifyOnPublish} onChange={(e) => setNotifyOnPublish(e.target.checked)} />
              Notify employees when published
            </label>
            {requiresAck && (
              <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
                <label className="text-slate-700">First reminder after days
                  <input type="number" min={0} value={firstReminderAfterDays} onChange={(e) => setFirstReminderAfterDays(e.target.value === '' ? '' : Number(e.target.value))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
                </label>
                <label className="text-slate-700">Repeat every days
                  <input type="number" min={0} value={repeatReminderEveryDays} onChange={(e) => setRepeatReminderEveryDays(e.target.value === '' ? '' : Number(e.target.value))} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
                </label>
                <label className="text-slate-700">Max reminders
                  <input type="number" min={0} value={maxReminders} onChange={(e) => setMaxReminders(Number(e.target.value) || 0)} className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2" />
                </label>
              </div>
            )}
            <div className="flex justify-between pt-1">
              <button type="button" onClick={() => setStep(1)} className="rounded-lg px-4 py-2 text-sm text-neutral-600">Back</button>
              <div className="flex gap-2">
                <button type="button" disabled={saving} onClick={() => void saveDraft(false)} className="rounded-lg border border-neutral-200 px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60">Save draft</button>
                <button
                  type="button"
                  disabled={saving || status === 'Archived'}
                  onClick={() => void saveDraft(true)}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {publishing ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
                  {publishing ? 'Publishing…' : 'Publish & distribute'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function FilterLevel({
  step,
  title,
  options,
  selected,
  onToggle,
}: {
  step: number;
  title: string;
  options: Array<{ id: number; label: string; count: number }>;
  selected: number[];
  onToggle: (id: number) => void;
}) {
  return (
    <div className="min-w-0">
      <div className="mb-2 flex items-center gap-2">
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-white">
          {step}
        </span>
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">{title}</span>
        {selected.length > 0 && (
          <span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
            {selected.length}
          </span>
        )}
      </div>
      <div className="max-h-32 space-y-1 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50/70 p-2">
        {options.map((item) => {
          const checked = selected.includes(item.id);
          return (
            <label
              key={item.id}
              className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition ${
                checked ? 'bg-white shadow-sm ring-1 ring-blue-200' : 'hover:bg-white/80'
              }`}
            >
              <input type="checkbox" checked={checked} onChange={() => onToggle(item.id)} className="rounded border-slate-300" />
              <span className="min-w-0 flex-1 truncate text-slate-800">{item.label}</span>
              <span className={`tabular-nums text-xs font-semibold ${checked ? 'text-blue-700' : 'text-slate-400'}`}>
                {item.count}
              </span>
            </label>
          );
        })}
        {options.length === 0 && <div className="px-2 py-3 text-xs text-slate-400">No options</div>}
      </div>
    </div>
  );
}

function EmployeePanel({
  title,
  subtitle,
  query,
  onQueryChange,
  employees,
  emptyText,
  accent,
  onRemove,
  headerAction,
}: {
  title: string;
  subtitle: string;
  query: string;
  onQueryChange: (value: string) => void;
  employees: Employee[];
  emptyText: string;
  accent: 'slate' | 'blue';
  onRemove?: (id: number) => void;
  headerAction?: React.ReactNode;
}) {
  const headerBg = accent === 'blue' ? 'bg-blue-50/80' : 'bg-slate-50';
  return (
    <div className="flex h-[260px] flex-col border-b border-slate-100 md:border-b-0">
      <div className={`shrink-0 border-b border-slate-100 px-4 py-3 ${headerBg}`}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
            <p className="text-xs text-slate-500">{subtitle}</p>
          </div>
          {headerAction}
        </div>
        <div className="relative mt-2">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search…"
            className="w-full rounded-lg border border-slate-200 bg-white py-1.5 pl-8 pr-2 text-sm"
          />
        </div>
      </div>
      <div className="min-h-0 flex-1 space-y-1 overflow-y-auto p-3">
        {employees.map((e) => (
          <div
            key={e.id}
            className="group flex items-center gap-2 rounded-xl border border-transparent px-2.5 py-2 hover:border-slate-200 hover:bg-slate-50"
          >
            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
              accent === 'blue' ? 'bg-blue-100 text-blue-800' : 'bg-slate-200 text-slate-700'
            }`}>
              {(e.firstName?.[0] || '?').toUpperCase()}
              {(e.lastName?.[0] || '').toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-slate-900">{employeeName(e)}</div>
              <div className="truncate text-xs text-slate-500">
                {e.profile?.locationName || e.location?.name || '—'}
                {e.profile?.positionName || e.position?.title ? ` · ${e.profile?.positionName || e.position?.title}` : ''}
              </div>
            </div>
            {onRemove ? (
              <button
                type="button"
                onClick={() => onRemove(e.id)}
                className="rounded-full p-1 text-slate-400 opacity-0 transition hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100"
                title="Remove"
              >
                <X className="h-4 w-4" />
              </button>
            ) : (
              <Check className="h-3.5 w-3.5 text-slate-300" />
            )}
          </div>
        ))}
        {employees.length === 0 && (
          <div className="flex h-full min-h-[100px] items-center justify-center px-4 text-center text-sm text-slate-400">
            {emptyText}
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentPolicyEditorPage;
